import React, { useEffect, useRef, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CircularProgress, InputAdornment, MenuItem, TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import useWipFirestoreStock from '../../hooks/useWipFirestoreStock';
import { formatMonitorNumber, sortWipByStatus } from '../../utils/wipMonitor';
import MonitorNavigation from './MonitorNavigation';

const statusColor = (status, primary) => ({
  EXPIRED: '#f4513f',
  'MUST USE': '#ffb52f',
  READY: '#16bcd2',
  PREPARING: '#37cc72',
}[status] || primary);


export default function StockTablePage({ title, category, embedded = false }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { items, loading, error, clock, scope, scopeReady } = useWipFirestoreStock();
  const [search, setSearch] = useState('');
  const [zone, setZone] = useState('');
  const [column, setColumn] = useState('all');
  const columns = { location: 'สถานที่', sku: 'รหัสสินค้า', skuName: 'ชื่อสินค้า', step: 'ขั้นตอน', batch: 'Batch', lot: 'Lot', weightKg: 'น้ำหนัก', agingHours: 'เวลา', status: 'สถานะ' };
  const rows = useMemo(() => {
    const query = search.trim().toUpperCase();
    return sortWipByStatus(items.filter((item) => (
      (!category || item.category === category)
      && (!zone || item.zone === zone)
      && (!query || query.split(/\s+/).every(term => (column === 'all' ? [...Object.keys(columns), 'zone', 'category'] : [column])
        .some(key => String(item[key] ?? '').toUpperCase().includes(term))))
    )));
  }, [category, items, search, zone, column]);


  const listRef = useRef(null);
  const [pageSize, setPageSize] = useState(5);
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [search, zone, column, category]);
  useEffect(() => {
    const element = listRef.current;
    if (!element) return undefined;
    const measure = () => {
      const row = element.firstElementChild;
      const rowHeight = row?.getBoundingClientRect().height || 80;
      const available = window.innerHeight - element.getBoundingClientRect().top - 72;
      setPageSize(Math.max(1, Math.min(10, Math.floor(available / (rowHeight + 8)))));
    };
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(element);
    if (element.parentElement) observer?.observe(element.parentElement);
    window.addEventListener('resize', measure);
    measure();
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, [loading, rows.length]);
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const activePage = Math.min(page, pageCount - 1);
  const visibleRows = rows.slice(activePage * pageSize, (activePage + 1) * pageSize);
  const small = { fontSize: 16, lineHeight: 1.4, color: '#bdcbd6' };
  const field = {
    width: '100%', minWidth: 0,
    '& .MuiInputBase-root': { fontSize: 18, height: 46, bgcolor: '#121c26', color: '#edf4f7', borderRadius: 1.5 },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#435867' },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#75cbd0' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#75cbd0' },
    '& .MuiSvgIcon-root': { color: '#a9c2cf' },
    '& input::placeholder': { color: '#a9bdca', opacity: 1 },
  };
  return <Box sx={{ bgcolor: '#0e161e', p: { xs: 1.5, md: 2 }, color: '#edf4f7' }}>
    {!embedded && <MonitorNavigation compact />}
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
      <Typography component="h1" sx={{ fontSize: 28, color: colors.primary[500], fontWeight: 600, mr: 'auto' }}>{category === 'RAW MAT' ? 'Raw Mat' : category || title}</Typography>
      <Typography sx={small}>{scope.plantId} / {scope.factory} · {rows.length} รายการ</Typography>
      <Typography sx={small}>{clock.toLocaleString('en-GB')}</Typography>
    </Box>
    {!scopeReady && <Alert severity="warning" sx={{ mb: 1 }}>กรุณาเลือก Plant และ Factory</Alert>}
    {error && <Alert severity="error" sx={{ mb: 1 }}>ไม่สามารถโหลดข้อมูลได้: {error}</Alert>}
    <Box sx={{ bgcolor: '#1b2530', border: '1px solid #344b59', borderRadius: 2, p: { xs: 1.5, md: 2 }, mb: 1.5 }}>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '180px 210px minmax(0, 1fr)' }, gap: 1.5, alignItems: 'end' }}>
      <Box>
      <Typography component="label" htmlFor="stock-zone" sx={{ ...small, display: 'block', mb: 0.5 }}>โซน</Typography>
      <TextField id="stock-zone" select size="small" value={zone} onChange={e => setZone(e.target.value)} sx={field} SelectProps={{ displayEmpty: true, inputProps: { 'aria-label': 'โซน' } }}>
        <MenuItem value="">ทุกโซน</MenuItem><MenuItem value="ดิบ">ดิบ</MenuItem><MenuItem value="สุก">สุก</MenuItem>
      </TextField>
      </Box>
      <Box>
      <Typography component="label" htmlFor="stock-column" sx={{ ...small, display: 'block', mb: 0.5 }}>ค้นหาใน</Typography>
      <TextField id="stock-column" select size="small" value={column} onChange={e => setColumn(e.target.value)} sx={field} SelectProps={{ inputProps: { 'aria-label': 'ค้นหาใน' } }}>
        <MenuItem value="all">ทุกคอลัมน์</MenuItem>{Object.entries(columns).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
      </TextField>
      </Box>
      <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}>
      <Typography component="label" htmlFor="stock-search" sx={{ ...small, display: 'block', mb: 0.5 }}>ค้นหาข้อมูล</Typography>
      <TextField id="stock-search" size="small" value={search} onChange={e => setSearch(e.target.value)} placeholder="ชื่อสินค้า, รหัสสินค้า, Lot หรือ Batch"
        inputProps={{ 'aria-label': 'ค้นหาสต็อก' }} sx={field}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
      </Box>
    </Box>
    </Box>
    {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : !rows.length ?
      <Box sx={{ py: 3, px: 2, textAlign: 'center', bgcolor: '#18222c', border: '1px dashed #344b59', borderRadius: 2 }}>
        <Inventory2OutlinedIcon sx={{ fontSize: 32, color: '#7d9ba9', mb: 0.5 }} />
        <Typography sx={{ fontSize: 18, color: '#edf4f7' }}>ไม่พบข้อมูลที่ตรงกับตัวกรอง</Typography>
        <Typography sx={small}>ลองเปลี่ยนโซนหรือคำค้นหา</Typography>
        {(search || zone || column !== 'all') && <Button sx={{ mt: 1, color: '#75cbd0' }} onClick={() => { setSearch(''); setZone(''); setColumn('all'); }}>ล้างตัวกรอง</Button>}
      </Box> : <>
      <Box ref={listRef} sx={{ display: 'grid', gap: 1 }}>
        {visibleRows.map(row => <Card key={row.id} sx={{ p: 1.25, bgcolor: '#1b222b', border: '1px solid #344b59', boxShadow: 'none', borderRadius: 1.5, color: '#edf4f7',
          display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr) minmax(0, 1fr)', md: 'minmax(0, 2fr) minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr) minmax(115px, 1fr)' }, gap: 1 }}>
          <Box sx={{ minWidth: 0 }}><Typography sx={{ fontSize: 18 }} noWrap title={row.sku}>{row.sku}</Typography><Typography sx={small} noWrap title={row.skuName}>{row.skuName}</Typography></Box>
          <Box sx={{ minWidth: 0 }}><Typography sx={{ fontSize: 18 }} noWrap title={row.location}>{row.location}</Typography><Typography sx={small} noWrap title={row.step}>{row.step}</Typography></Box>
          <Box sx={{ minWidth: 0 }}><Typography sx={small} noWrap title={row.lot}>Lot {row.lot}</Typography><Typography sx={small} noWrap title={row.batch}>Batch {row.batch}</Typography></Box>
          <Box sx={{ minWidth: 0 }}><Typography sx={{ fontSize: 18 }} noWrap title={String(row.weightKg)}>{formatMonitorNumber(row.weightKg)} kg</Typography><Typography sx={small}>{formatMonitorNumber(row.agingHours, 1)} ชม.</Typography></Box>
          <Box sx={{ alignSelf: 'center', gridColumn: { xs: '1 / -1', md: 'auto' } }}><Typography sx={{ fontSize: 16, fontWeight: 600, color: statusColor(row.status, colors.primary[500]) }}>{row.status}</Typography></Box>
        </Card>)}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1 }}>
        <Typography sx={small}>{activePage * pageSize + 1}–{Math.min((activePage + 1) * pageSize, rows.length)} จาก {rows.length}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button disabled={activePage === 0} onClick={() => setPage(activePage - 1)} aria-label="หน้าก่อนหน้า">ก่อนหน้า</Button>
          <Typography sx={small}>{activePage + 1} / {pageCount}</Typography>
          <Button disabled={activePage + 1 >= pageCount} onClick={() => setPage(activePage + 1)} aria-label="หน้าถัดไป">ถัดไป</Button>
        </Box>
      </Box>
    </>}
  </Box>;
}

